import { useState } from 'react'

import {
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Skeleton,
  Switch,
  Text,
  VStack,
} from '@chakra-ui/react'

import api from '@/api'
import { GoogleIcon } from '@/assets/icons'
import { isWorkEmail } from '@/helpers/team-name/is-work-email'
import { useUser } from '@/react-query/user'
import {
  useUpdateUserSettings,
  useUserSettings,
} from '@/react-query/user-settings'
import Card from '@/views/components/Card/Card'

export const NotetakerSettings = () => {
  return (
    <Card w={'100%'} p={6} px={10}>
      <Heading size={'md'}>Notetaker bot</Heading>
      <VStack mt={6} alignItems={'flex-start'} gap={4} w={'100%'}>
        <GoogleCalendar />
      </VStack>
    </Card>
  )
}

const ConnectGoogleCalendar = () => {
  const user = useUser()
  const [isLoading, setIsLoading] = useState(false)

  return user?.data?.GoogleData ? (
    <HStack alignItems={'center'} columnGap={6} wrap={'wrap'}>
      <VStack alignItems={'flex-start'}>
        <HStack>
          <GoogleIcon />
          <Text>
            Calendar <b>{user.data.GoogleData.email}</b> connected
          </Text>
        </HStack>
      </VStack>
      <Button
        isLoading={isLoading}
        variant={'brand-solid-white'}
        onClick={async () => {
          setIsLoading(true)
          await api.googleLogOut()
          await user.refetch()
          setIsLoading(false)
        }}
      >
        Disconnect
      </Button>
    </HStack>
  ) : (
    <Flex alignItems={'center'} gap={8} rowGap={4} w={'100%'} wrap={'wrap'}>
      <Button
        isLoading={isLoading}
        variant={'brand-solid-white'}
        onClick={async () => {
          setIsLoading(true)

          const { authUrl } = (
            await api.googleAuth({
              redirectURI: window.location.href,
            })
          ).data

          window.location.href = authUrl
        }}
        whiteSpace={'normal'}
      >
        <GoogleIcon mr={4} />
        Connect Google Calendar
      </Button>
      <Text>
        The bot will join all new events on this calendar automatically
      </Text>
    </Flex>
  )
}

const GoogleCalendar = () => {
  const user = useUser()

  return (
    <>
      <ConnectGoogleCalendar />
      {isWorkEmail(user.data?.email || '') && (
        <EnableInternalGoogleCalendarMeetings />
      )}
      <EnableRecurringGoogleCalendarMeetings />
    </>
  )
}

const EnableInternalGoogleCalendarMeetings: React.FC = () => {
  const userSettings = useUserSettings()
  const updateUserSettings = useUpdateUserSettings()
  const user = useUser()

  const userDomain = user.data?.email?.split('@')[1]

  return (
    <FormControl
      display={'inline-flex'}
      alignItems={'center'}
      w={'auto'}
      mt={4}
    >
      <Skeleton
        rounded={'1.25rem'}
        isLoaded={!userSettings.isLoading}
        startColor="#f5f5f5"
        endColor="#d7d7d7"
      >
        <Switch
          id="enable-internal-google-calendar-meetings"
          isChecked={!userSettings.data?.enableInternalGoogleCalendarMeetings}
          onChange={(event) => {
            event.preventDefault()
            updateUserSettings.mutate({
              enableInternalGoogleCalendarMeetings:
                !userSettings.data?.enableInternalGoogleCalendarMeetings,
            })
          }}
          size={'lg'}
          variant={'brand-black'}
          isDisabled={userSettings.isLoading}
        />
      </Skeleton>
      <FormLabel
        htmlFor="enable-internal-google-calendar-meetings"
        mb="0"
        ml={'4'}
      >
        Skip events that are fully internal (all guests from <b>{userDomain}</b>
        )
      </FormLabel>
    </FormControl>
  )
}

const EnableRecurringGoogleCalendarMeetings: React.FC = () => {
  const userSettings = useUserSettings()
  const updateUserSettings = useUpdateUserSettings()
  const user = useUser()

  return (
    <FormControl
      display={'inline-flex'}
      alignItems={'center'}
      w={'auto'}
      mt={4}
    >
      <Skeleton
        rounded={'1.25rem'}
        isLoaded={!userSettings.isLoading}
        startColor="#f5f5f5"
        endColor="#d7d7d7"
      >
        <Switch
          id="enable-recurring-google-calendar-meetings"
          isChecked={!userSettings.data?.enableRecurringGoogleCalendarMeetings}
          onChange={(event) => {
            event.preventDefault()
            updateUserSettings.mutate({
              enableRecurringGoogleCalendarMeetings:
                !userSettings.data?.enableRecurringGoogleCalendarMeetings,
            })
          }}
          size={'lg'}
          variant={'brand-black'}
          isDisabled={userSettings.isLoading}
        />
      </Skeleton>
      <FormLabel
        htmlFor="enable-recurring-google-calendar-meetings"
        mb="0"
        ml={'4'}
      >
        Skip recurring events
      </FormLabel>
    </FormControl>
  )
}
